import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTags } from '../services/tagService';
import { useCompanySettings } from './CompanySettingsContext';

// Create context
const TagContext = createContext();

/**
 * TagProvider component
 * Provides tag data and management functions to child components
 */
export const TagProvider = ({ children }) => {
  const { selectedStore } = useCompanySettings();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  
  // Fetch tags
  const fetchTags = useCallback(async (forceRefresh = false) => {
    // Skip if no store is selected
    if (!selectedStore || selectedStore === 'no-store') {
      return;
    }
    
    // Skip if tags were recently fetched (within 5 minutes) and not forcing refresh
    const now = new Date();
    if (
      !forceRefresh &&
      lastFetched &&
      (now.getTime() - lastFetched.getTime()) < 5 * 60 * 1000
    ) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const tagData = await getTags(selectedStore);
      
      setTags(tagData);
      setLastFetched(now);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedStore, lastFetched]);
  
  // Fetch tags when store changes
  useEffect(() => {
    fetchTags();
  }, [selectedStore, fetchTags]);
  
  // Get a tag by ID
  const getTagById = useCallback((tagId) => {
    return tags.find(tag => tag.id === tagId) || null;
  }, [tags]);
  
  // Get tags by IDs
  const getTagsByIds = useCallback((tagIds) => {
    return tags.filter(tag => tagIds.includes(tag.id));
  }, [tags]);
  
  // Context value
  const value = {
    tags,
    loading,
    error,
    fetchTags,
    getTagById,
    getTagsByIds
  };
  
  return (
    <TagContext.Provider value={value}>
      {children}
    </TagContext.Provider>
  );
};

/**
 * Custom hook to use the tag context
 */
export const useTags = () => {
  const context = useContext(TagContext);
  
  if (!context) {
    throw new Error('useTags must be used within a TagProvider');
  }
  
  return context;
};

export default TagContext;

